import { makeStyles } from '@material-ui/core';

const TABLE_HEADER_BACKGROUND = '#EEF3FF';

const useStyles = makeStyles(theme => ({
  draftNotice: {
    alignItems: 'center',
    color: theme.palette.warning.dark,
    display: 'inline-flex',
    fontSize: '0.875rem',
    fontWeight: 500,
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  metricsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  metricTile: {
    flex: '1 1 160px',
    minWidth: 0,
  },
  metricCard: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    height: '100%',
    padding: theme.spacing(2),
  },
  metricLabel: {
    fontWeight: 500,
  },
  metricValueLine: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  metricValue: {
    fontWeight: 600,
    lineHeight: 1.1,
  },
  detailGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: '2fr 1fr',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  panel: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2.5),
  },
  panelTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  readGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  readField: {
    minWidth: 0,
  },
  readFieldFull: {
    gridColumn: '1 / -1',
  },
  readLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  },
  readValue: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  categoryBlock: {
    marginBottom: theme.spacing(2),
  },
  tagRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  categoryChip: {
    backgroundColor: '#EEF3FF',
    fontWeight: 500,
  },
  timelineItem: {
    borderLeft: `2px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  timelineTitle: {
    fontWeight: 600,
  },
  timelineMeta: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    marginBottom: theme.spacing(0.5),
  },
  sectionHeader: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  sectionHelp: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginTop: theme.spacing(0.5),
  },
  toggleLine: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: '0.875rem',
    gap: theme.spacing(1),
  },
  purposeStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  purposeCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  purposeSummary: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    flexWrap: 'wrap',
    fontWeight: 500,
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    listStyle: 'none',
    padding: theme.spacing(1.5, 2),
    '&::-webkit-details-marker': {
      display: 'none',
    },
  },
  purposeMain: {
    flex: '1 1 240px',
    minWidth: 0,
  },
  purposeMeta: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  purposeContent: {
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  removedPurpose: {
    opacity: 0.72,
  },
  removedBadge: {
    backgroundColor: '#F3F4F6',
    fontWeight: 600,
  },
  filterRow: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  filterSelect: {
    minWidth: 200,
  },
  tableScrollWrapper: {
    overflowX: 'auto',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
    '& > div': {
      minWidth: 'max-content',
      overflow: 'visible',
    },
    '& table': {
      minWidth: 1180,
      tableLayout: 'auto',
    },
    '& thead th': {
      whiteSpace: 'nowrap',
    },
    // Override Table's td z-index:-1 so row clicks are not lost under the sticky actions column.
    '& table tbody td': {
      position: 'relative',
      zIndex: 1,
    },
    '& table th:nth-last-child(1), & table td:nth-last-child(1)': {
      boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.06)',
      minWidth: 56,
      padding: theme.spacing(1, 0.5),
      position: 'sticky',
      right: 0,
      verticalAlign: 'middle',
      zIndex: 2,
    },
    '& table thead th:nth-last-child(1)': {
      background: TABLE_HEADER_BACKGROUND,
      borderTopRightRadius: 8,
      boxShadow: '-4px 0 8px rgba(16, 24, 40, 0.06)',
      zIndex: 3,
    },
    '& table tbody td:nth-last-child(1)': {
      background: theme.palette.common.white,
      zIndex: 2,
      '&::before': {
        background: 'linear-gradient(to right, rgba(0, 0, 0, 0.06), transparent)',
        bottom: 0,
        content: '""',
        left: 0,
        pointerEvents: 'none',
        position: 'absolute',
        top: 0,
        width: 8,
        zIndex: 0,
      },
      '& > span': {
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1,
      },
    },
  },
  statusDot: {
    borderRadius: '50%',
    display: 'inline-block',
    height: 8,
    marginRight: theme.spacing(1),
    width: 8,
  },
  statusDotActive: {
    backgroundColor: theme.palette.success.main,
  },
  statusDotWarning: {
    backgroundColor: theme.palette.warning.main,
  },
  statusDotDanger: {
    backgroundColor: theme.palette.error.main,
  },
  statusDotInfo: {
    backgroundColor: theme.palette.info.main,
  },
  dpiaBadge: {
    borderRadius: 999,
    display: 'inline-block',
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: theme.spacing(0.5, 1),
  },
  dpiaBadgeInfo: {
    backgroundColor: '#E8F1FF',
    color: '#1B4F91',
  },
  dpiaBadgeWarn: {
    backgroundColor: '#FFF4E5',
    color: '#9A5B00',
  },
  dpiaBadgeSuccess: {
    backgroundColor: '#E7F6ED',
    color: '#1F6B3B',
  },
  dpiaBadgeDanger: {
    backgroundColor: '#FDECEC',
    color: '#9B1C1C',
  },
  actionsCell: {
    display: 'flex',
    justifyContent: 'center',
  },
  emptyState: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(3, 0),
    textAlign: 'center',
  },
  forbiddenState: {
    marginTop: theme.spacing(4),
    textAlign: 'center',
  },
  errorBanner: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  sectionTitleRow: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: theme.spacing(0.5),
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
}));

export default useStyles;
